/**
 * Staff Summary Cards Component
 * Displays summary statistics for staff records
 */
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, CheckCircle, Clock, AlertTriangle, Printer, Download } from "lucide-react";
import { format } from "date-fns";
import jsPDF from "jspdf";
import "jspdf-autotable";

// Clearance type labels for display
const CLEARANCE_LABELS = [
  "Child Abuse Clearance",
  "Criminal Record Check", 
  "FBI Clearance",
  "NSOR Check",
  "Disclosure Statement",
  "Mandated Reporter",
  "Fire Safety Training",
  "Emergency Preparedness",
  "Health & Safety",
  "First Aid/CPR",
  "Shaken Baby Policy",
  "Prof. Development"
];

// One-time clearance positions: Disclosure (4), Health & Safety (8), Shaken Baby Policy (10)
const ONE_TIME_INDICES = new Set([4, 8, 10]);
// Prof Dev position
const PROF_DEV_IDX = 11;

// Parse dates stored as either YYYY-MM-DD or MM/DD/YYYY
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const d = new Date(dateStr + "T12:00:00");
    return isNaN(d.getTime()) ? null : d;
  }
  // MM/DD/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(dateStr)) {
    const [month, day, year] = dateStr.split("/");
    const d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

export const StaffSummaryCards = ({ records, uploadMap = {} }) => {
  const [expiringDialogOpen, setExpiringDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  const now = new Date();
  
  const getClearances = (record) => [
    record.childAbuseClearance,
    record.criminalRecordCheck,
    record.fbiClearance,
    record.nsorCheck,
    record.disclosureStatement,
    record.mandatedReporterTraining,
    record.fireSafetyTraining,
    record.emergencyPreparedness,
    record.healthSafetyBasics || record.healthHumanServices,
    record.pediatricFirstAidCPR,
    record.shakenBabyPolicy,
    record.profDevTraining
  ];

  const isCompleted = (c, idx, recordId) => {
    // Prof Dev: hours-only rule (matches backend resolve_staff_req_status) — no
    // generic certificate upload requirement.
    if (idx === PROF_DEV_IDX) return (c?.currentCycleHours || 0) >= 12;
    // One-time: if completion date exists → always Completed, ignore any stored expiry
    if (ONE_TIME_INDICES.has(idx)) return !!c?.completionDate;
    // Regular: must have completion date and not be expired
    if (!c?.completionDate) return false;
    // Check stored status field first — if "expired" it's not complete regardless of dates
    if ((c?.status || "").toLowerCase() === "expired") return false;
    if (c?.expirationDate) {
      const exp = parseDate(c.expirationDate);
      return exp ? now <= exp : true;
    }
    return true;
  };

  const allClearCount = records.filter(r =>
    getClearances(r).every((c, idx) => isCompleted(c, idx, r.id))
  ).length;

  // Expiring Soon: regular clearances expiring within 30 days (skip one-time and Prof Dev)
  const getExpiringDetails = () => {
    const details = [];
    records.forEach(r => {
      const clearances = getClearances(r);
      clearances.forEach((c, idx) => {
        if (ONE_TIME_INDICES.has(idx) || idx === PROF_DEV_IDX) return;
        if (c?.completionDate && c?.expirationDate) {
          const exp = parseDate(c.expirationDate);
          if (!exp) return;
          const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
          if (days > 0 && days <= 30) {
            details.push({ staffName: r.name, clearanceType: CLEARANCE_LABELS[idx], expirationDate: exp, daysLeft: days });
          }
        }
      });
    });
    return details.sort((a, b) => a.daysLeft - b.daysLeft);
  };

  const expiringDetails = getExpiringDetails();
  const expiringCount = new Set(expiringDetails.map(d => d.staffName)).size;

  // Action Needed: no date, or expired (regular only), or Prof Dev < 12 hrs
  const getActionNeededDetails = () => {
    const details = [];
    records.forEach(r => {
      const clearances = getClearances(r);
      clearances.forEach((c, idx) => {
        // Prof Dev: action needed if hours < 12 (hours-only rule, no certificate upload required)
        if (idx === PROF_DEV_IDX) {
          const hrs = c?.currentCycleHours || 0;
          if (hrs < 12) {
            details.push({ staffName: r.name, clearanceType: CLEARANCE_LABELS[idx], reason: hrs > 0 ? `${hrs} / 12 hrs completed` : "Not completed" });
          }
          return;
        }
        // One-time: action needed only if NO completion date
        if (ONE_TIME_INDICES.has(idx)) {
          if (!c?.completionDate) {
            details.push({ staffName: r.name, clearanceType: CLEARANCE_LABELS[idx], reason: "Not On File" });
          }
          return;
        }
        // Regular: no completion date
        if (!c?.completionDate) {
          details.push({ staffName: r.name, clearanceType: CLEARANCE_LABELS[idx], reason: "Not completed" });
          return;
        }
        // Regular: check expiration — use stored expirationDate OR fall back to stored status field
        const storedStatus = (c?.status || "").toLowerCase();
        if (c?.expirationDate) {
          const exp = parseDate(c.expirationDate);
          if (exp && now > exp) {
            details.push({ staffName: r.name, clearanceType: CLEARANCE_LABELS[idx], reason: `Expired ${format(exp, "MMM d, yyyy")}` });
          }
        } else if (storedStatus === "expired") {
          // No expiration date stored but status field says expired — still flag it
          details.push({ staffName: r.name, clearanceType: CLEARANCE_LABELS[idx], reason: "Expired (renewal required)" });
        }
      });
    });
    return details;
  };

  const actionNeededDetails = getActionNeededDetails();
  const actionNeededCount = new Set(actionNeededDetails.map(d => d.staffName)).size;

  // Pending: clearances with no completion date (all types); Prof Dev < 12 hrs
  const getPendingDetails = () => {
    const details = [];
    records.forEach(r => {
      const clearances = getClearances(r);
      clearances.forEach((c, idx) => {
        if (idx === PROF_DEV_IDX) {
          if ((c?.currentCycleHours || 0) < 12) {
            details.push({ staffName: r.name, clearanceType: CLEARANCE_LABELS[idx] });
          }
          return;
        }
        if (!c?.completionDate) {
          details.push({ staffName: r.name, clearanceType: CLEARANCE_LABELS[idx] });
        }
      });
    });
    return details;
  };

  const pendingDetails = getPendingDetails();
  const pendingCount = new Set(pendingDetails.map(d => d.staffName)).size;

  // Print Expiring Soon report
  const handlePrintExpiring = () => {
    const printContent = `
      <html>
        <head>
          <title>Clearances Expiring Soon - ${format(now, "MMM d, yyyy")}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #d97706; border-bottom: 2px solid #d97706; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #fef3c7; color: #92400e; text-align: left; padding: 12px; border: 1px solid #fcd34d; }
            td { padding: 10px; border: 1px solid #fcd34d; }
            tr:nth-child(even) { background: #fffbeb; }
            .urgent { color: #dc2626; font-weight: bold; }
            .warning { color: #ea580c; font-weight: bold; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <h1>⏰ Clearances Expiring Within 30 Days</h1>
          <p>Generated: ${format(now, "MMMM d, yyyy 'at' h:mm a")}</p>
          <p>Total: ${expiringDetails.length} clearance(s) for ${expiringCount} staff member(s)</p>
          <table>
            <thead>
              <tr>
                <th>Staff Member</th>
                <th>Clearance Type</th>
                <th>Expiration Date</th>
                <th>Days Left</th>
              </tr>
            </thead>
            <tbody>
              ${expiringDetails.map(item => `
                <tr>
                  <td>${item.staffName}</td>
                  <td>${item.clearanceType}</td>
                  <td>${format(item.expirationDate, "MMM d, yyyy")}</td>
                  <td class="${item.daysLeft <= 7 ? 'urgent' : item.daysLeft <= 14 ? 'warning' : ''}">${item.daysLeft} days</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">Report generated by Simple Sign In Childcare Management System</div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // PDF Download for Expiring Soon
  const handleDownloadExpiringPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setTextColor(217, 119, 6);
    doc.text("Clearances Expiring Within 30 Days", 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${format(now, "MMMM d, yyyy 'at' h:mm a")}`, 14, 28);
    doc.text(`Total: ${expiringDetails.length} clearance(s) for ${expiringCount} staff member(s)`, 14, 34);

    const tableData = expiringDetails.map(item => [
      item.staffName,
      item.clearanceType,
      format(item.expirationDate, "MMM d, yyyy"),
      `${item.daysLeft} days`
    ]);

    doc.autoTable({
      startY: 42,
      head: [["Staff Member", "Clearance Type", "Expiration Date", "Days Left"]],
      body: tableData,
      headStyles: { fillColor: [254, 243, 199], textColor: [146, 64, 14] },
      alternateRowStyles: { fillColor: [255, 251, 235] },
      styles: { fontSize: 9 }
    });

    doc.save(`Expiring_Clearances_${format(now, "yyyy-MM-dd")}.pdf`);
  };

  // Print Action Needed report
  const handlePrintAction = () => {
    const printContent = `
      <html>
        <head>
          <title>Action Needed Report - ${format(now, "MMM d, yyyy")}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #dc2626; border-bottom: 2px solid #dc2626; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #fee2e2; color: #991b1b; text-align: left; padding: 12px; border: 1px solid #fca5a5; }
            td { padding: 10px; border: 1px solid #fca5a5; }
            tr:nth-child(even) { background: #fef2f2; }
            .expired { color: #dc2626; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <h1>⚠️ Action Needed For ${actionNeededCount} Staff Member${actionNeededCount !== 1 ? 's' : ''}</h1>
          <p>Generated: ${format(now, "MMMM d, yyyy 'at' h:mm a")}</p>
          <p>Total Items Requiring Action: ${actionNeededDetails.length}</p>
          <table>
            <thead>
              <tr>
                <th>Staff Member</th>
                <th>Clearance Type</th>
                <th>Status/Reason</th>
              </tr>
            </thead>
            <tbody>
              ${actionNeededDetails.map(item => `
                <tr>
                  <td>${item.staffName}</td>
                  <td>${item.clearanceType}</td>
                  <td class="${item.reason.includes('Expired') ? 'expired' : ''}">${item.reason}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">Report generated by Simple Sign In Childcare Management System</div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // PDF Download for Action Needed
  const handleDownloadActionPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setTextColor(220, 38, 38);
    doc.text(`Action Needed For ${actionNeededCount} Staff Member${actionNeededCount !== 1 ? 's' : ''}`, 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${format(now, "MMMM d, yyyy 'at' h:mm a")}`, 14, 28);
    doc.text(`Total Items Requiring Action: ${actionNeededDetails.length}`, 14, 34);

    const tableData = actionNeededDetails.map(item => [
      item.staffName,
      item.clearanceType,
      item.reason
    ]);

    doc.autoTable({
      startY: 42,
      head: [["Staff Member", "Clearance Type", "Status/Reason"]],
      body: tableData,
      headStyles: { fillColor: [254, 226, 226], textColor: [153, 27, 27] },
      alternateRowStyles: { fillColor: [254, 242, 242] },
      styles: { fontSize: 9 }
    });

    doc.save(`Action_Needed_Report_${format(now, "yyyy-MM-dd")}.pdf`);
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-white/80 backdrop-blur-sm border-2 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-amber-600">Total Staff</p>
                <p className="text-2xl font-bold text-amber-800">{records.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/80 backdrop-blur-sm border-2 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-amber-600">All Clear</p>
                <p className="text-2xl font-bold text-green-600">{allClearCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending - Clickable Card */}
        <Card 
          className="bg-white/80 backdrop-blur-sm border-2 border-purple-200 cursor-pointer hover:border-purple-400 hover:shadow-lg transition-all"
          onClick={() => pendingDetails.length > 0 && setPendingDialogOpen(true)}
          data-testid="pending-card"
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-amber-600">Pending</p>
                <p className="text-2xl font-bold text-purple-600">{pendingCount}</p>
                {pendingCount > 0 && (
                  <p className="text-xs text-purple-500 mt-1">
                    {pendingCount} Staff Member{pendingCount !== 1 ? 's' : ''} — Click to view
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Expiring Soon - Clickable Card */}
        <Card 
          className="bg-white/80 backdrop-blur-sm border-2 border-yellow-200 cursor-pointer hover:border-yellow-400 hover:shadow-lg transition-all"
          onClick={() => expiringDetails.length > 0 && setExpiringDialogOpen(true)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-amber-600">Expiring Soon</p>
                <p className="text-2xl font-bold text-yellow-600">{expiringCount}</p>
                <p className="text-xs text-yellow-600 mt-1">
                  Within 30 days
                </p>
                {expiringDetails.length > 0 && (
                  <p className="text-xs text-yellow-500 mt-1">Click to view details</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Action Needed - Clickable Card */}
        <Card 
          className="bg-white/80 backdrop-blur-sm border-2 border-red-200 cursor-pointer hover:border-red-400 hover:shadow-lg transition-all"
          onClick={() => actionNeededDetails.length > 0 && setActionDialogOpen(true)}
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-amber-600">Action Needed</p>
                <p className="text-2xl font-bold text-red-600">{actionNeededCount}</p>
                {actionNeededCount > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    For {actionNeededCount} Staff Member{actionNeededCount !== 1 ? 's' : ''}
                  </p>
                )}
                {actionNeededDetails.length > 0 && (
                  <p className="text-xs text-red-500 mt-1">Click to view details</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Dialog */}
      <Dialog open={pendingDialogOpen} onOpenChange={setPendingDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Clock className="w-5 h-5" />
              Pending Clearances — {pendingCount} Staff Member{pendingCount !== 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-3">
              {pendingDetails.map((item, idx) => (
                <div key={idx} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-purple-800">{item.staffName}</p>
                      <p className="text-sm text-purple-700">{item.clearanceType}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-purple-600">Pending</p>
                    </div>
                  </div>
                </div>
              ))}
              {pendingDetails.length === 0 && (
                <p className="text-center text-gray-500 py-4">No pending clearances</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expiring Soon Dialog */}
      <Dialog open={expiringDialogOpen} onOpenChange={setExpiringDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-700">
              <Clock className="w-5 h-5" />
              Clearances Expiring Within 30 Days
            </DialogTitle>
          </DialogHeader>
          
          {/* Print and Download buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              onClick={handlePrintExpiring}
              variant="outline"
              size="sm"
              className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button
              onClick={handleDownloadExpiringPDF}
              variant="outline"
              size="sm"
              className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-3">
              {expiringDetails.map((item, idx) => (
                <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-yellow-800">{item.staffName}</p>
                      <p className="text-sm text-yellow-700">{item.clearanceType}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${item.daysLeft <= 7 ? 'text-red-600' : item.daysLeft <= 14 ? 'text-orange-600' : 'text-yellow-600'}`}>
                        {item.daysLeft} day{item.daysLeft !== 1 ? 's' : ''} left
                      </p>
                      <p className="text-xs text-yellow-600">
                        Expires {format(item.expirationDate, "MMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {expiringDetails.length === 0 && (
                <p className="text-center text-gray-500 py-4">No clearances expiring within 30 days</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Needed Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Action Needed For {actionNeededCount} Staff Member{actionNeededCount !== 1 ? 's' : ''}
            </DialogTitle>
          </DialogHeader>
          
          {/* Print and Download buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              onClick={handlePrintAction}
              variant="outline"
              size="sm"
              className="border-red-400 text-red-700 hover:bg-red-50"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button
              onClick={handleDownloadActionPDF}
              variant="outline"
              size="sm"
              className="border-red-400 text-red-700 hover:bg-red-50"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            <div className="space-y-3">
              {actionNeededDetails.map((item, idx) => (
                <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-red-800">{item.staffName}</p>
                      <p className="text-sm text-red-700">{item.clearanceType}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-red-600">{item.reason}</p>
                    </div>
                  </div>
                </div>
              ))}
              {actionNeededDetails.length === 0 && (
                <p className="text-center text-gray-500 py-4">No action needed</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StaffSummaryCards;
